import { Group, MantineProvider } from '@mantine/core';
import _sum from 'lodash/sum';
import type { DataTableColumn } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';
import { useMemo } from 'react';
import type { Noop } from 'react-hook-form';
import { type Control } from 'react-hook-form';

import type {
  EstimationModelItemGroupResponse,
  EstimationModelResponse,
  MethodologyResponse,
} from '@/api/schema/data-contracts';
import AssumptionIcon from '@/assets/assum-icon-noparam.svg?react';
import ConversationIcon from '@/assets/conversation-icon.svg?react';
import DetailIcon from '@/assets/detail-icon-noparam.svg?react';
import PlusCircleIcon from '@/assets/plus-circle-icon.svg?react';
import { Button } from '@/components/atoms/Button';
import type { CustomDropdownOption } from '@/components/atoms/CustomDropdown';
import { DebugHighlighter } from '@/components/atoms/DebugHighlighter';
import { DropdownFormField } from '@/components/atoms/DropdownFormField';
import { FormattedNumber } from '@/components/atoms/FormattedNumber';
import { InlineEditableText } from '@/components/atoms/InlineEditableText';
import { NumericFormField } from '@/components/atoms/NumericFormField';
import { SwitchFormField } from '@/components/atoms/SwitchFormField';
import { UserFullName } from '@/components/UserFullName';
import { stripHtml } from '@/helpers/estimation';
import { useMessages } from '@/hooks/messages';
import { isTemporaryId } from '@/utils/generators';
import { sortCostTypes, sortExpressions, sortResourceTypes } from '@/utils/sorting';

import type {
  CommentModel,
  CostModel,
  EstimationGroupActionCallback,
  EstimationItemActionCallback,
  EstimationItemIsIncludedActionCallback,
  EstimationItemIsIncludedActionPromiseCallback,
  EstimationItemMethodologyChangeCallback,
  EstimationItemsEditFormModel,
  EstimationItemsFormModelRow,
  ResourceEstimationItemsTableRecord,
} from '../model';
import { ResourceColumnHeader } from '../ResourceColumnHeader';
import { mapFormModelItemWithOriginalData } from './formModelItemWithOriginalDataMapper';
import { estimationItemsTableMessageDefinitions } from './messages';
import type { FormModelItemWithOriginalData } from './model';

export interface EstimationItemsTableProps {
  control: Control<EstimationItemsEditFormModel>;
  group?: EstimationModelItemGroupResponse;
  formModelEstimationItems: EstimationItemsFormModelRow[];
  estimationItems: ResourceEstimationItemsTableRecord[];
  costs?: CostModel;
  comments?: CommentModel[];
  estimationModel: EstimationModelResponse;
  methodologies: MethodologyResponse[];
  onCommentsClicked: EstimationItemActionCallback;
  onDetailsClicked: EstimationItemActionCallback;
  onAssumptionsClicked: EstimationItemActionCallback;
  onIncludeClicked?: EstimationItemIsIncludedActionPromiseCallback;
  onIncludeChanged?: EstimationItemIsIncludedActionCallback;
  onAddPhaseClicked?: EstimationGroupActionCallback;
  onBeforeMethodologyChange?: () => Promise<boolean>;
  onMethodologyChange?: EstimationItemMethodologyChangeCallback;
  onResourceFieldBlur?: Noop;
  showButtons?: boolean;
  showResponsible?: boolean;
  showMethodology?: boolean;
  showParameters?: boolean;
  showCosts?: boolean;
  showResources?: true;
  isReadOnly: boolean;
  calculatedResultByEstimationItemId: Map<string, number>;
  searchText: string;
  showEstimateColumn?: boolean;
  hiddenCostTypeColumns?: string[];
  onResourceColumnHeaderRename?: (resourceTypeId: string, newName: string) => Promise<void>;
  resourceDisplayNames?: Record<string, string>;
  onItemRename?: (estimationItemId: string, newName: string) => Promise<void>;
}

function getDistinctParameterNamesSorted(
  estimationItems: ResourceEstimationItemsTableRecord[],
  formModelEstimationItems: EstimationItemsFormModelRow[],
  methodologies: MethodologyResponse[],
  group: EstimationModelItemGroupResponse | undefined,
) {
  const paramNames: string[] = [];

  if (!formModelEstimationItems) {
    throw new Error('Cannot be undefined');
  }

  const relevantMethodologyIds = formModelEstimationItems
    .filter((x) => x.groupId === group?.id)
    .filter((x) => x.methodologyId)
    .map((x) => x.methodologyId!);

  // methodology params first
  methodologies
    .filter((methodology) => relevantMethodologyIds.includes(methodology.id))
    .sort(sortExpressions.byOrder)
    .forEach((methodology) => {
      methodology.defaultParameters
        .filter((methodologyParameter) => !paramNames.includes(methodologyParameter.label))
        .sort(sortExpressions.byOrder)
        .forEach((x) => paramNames.push(x.label));
    });

  // estimation model item params
  estimationItems.forEach((estimationItem) => {
    estimationItem.params
      .filter((x) => !paramNames.includes(x.parameterName))
      .filter((x) => !!x.estimationModelItemParameterId)
      .sort(sortExpressions.byOrder)
      .forEach((x) => paramNames.push(x.parameterName));
  });

  return paramNames;
}

const EstimationItemsTable: React.FC<EstimationItemsTableProps> = ({
  control,
  group,
  formModelEstimationItems,
  estimationItems = [],
  estimationModel,
  methodologies,
  costs,
  hiddenCostTypeColumns,
  onAssumptionsClicked,
  onCommentsClicked,
  onDetailsClicked,
  onIncludeClicked,
  onIncludeChanged,
  onAddPhaseClicked,
  onBeforeMethodologyChange,
  onMethodologyChange,
  onResourceFieldBlur,
  showButtons,
  showResponsible,
  showMethodology,
  showParameters,
  showResources,
  showCosts,
  isReadOnly,
  calculatedResultByEstimationItemId,
  searchText,
  showEstimateColumn = false,
  onResourceColumnHeaderRename,
  resourceDisplayNames,
  onItemRename,
}: EstimationItemsTableProps) => {
  const messages = useMessages(estimationItemsTableMessageDefinitions);

  if (!formModelEstimationItems) {
    throw new Error('Form model estimationItems is undefined');
  }

  const formModelItemsWithOriginalData = useMemo(
    () => mapFormModelItemWithOriginalData(formModelEstimationItems, estimationItems, group),
    [formModelEstimationItems, estimationItems, group],
  );

  const distinctParameterNamesSorted = useMemo(
    () =>
      getDistinctParameterNamesSorted(
        estimationItems,
        formModelEstimationItems,
        methodologies,
        group,
      ),
    [estimationItems, formModelEstimationItems, methodologies, group],
  );

  const methodologyDropdownOptions: CustomDropdownOption[] = useMemo(
    () =>
      methodologies.sort(sortExpressions.byOrder).map((x) => ({
        label: x.name,
        value: x.id,
      })),
    [methodologies],
  );

  // A phase estimated through its sub-items ("Detailed Estimation" elements) holds no parameter
  // values of its own, so its input is not rendered. Show the sub-items' rolled-up sum in that cell
  // instead of leaving it blank, and let the column footer total it. EWM-987.
  const getSubItemsParameterSum = (
    item: FormModelItemWithOriginalData,
    parameterName: string,
  ): number | undefined =>
    item.originalData &&
    !item.originalData.isParentBasedCalculation &&
    item.originalData.childrenCount > 0
      ? (item.originalData.subItemsParamValueSums.get(parameterName) ?? 0)
      : undefined;

  const parametersColumns: DataTableColumn<FormModelItemWithOriginalData>[] =
    distinctParameterNamesSorted.length === 0
      ? [
          {
            accessor: 'estimationItemsParametersPlaceholder',
            title: '',
            noWrap: true,
            textAlign: 'right',
            hidden: !showParameters,
            render: () => null,
          },
        ]
      : distinctParameterNamesSorted.map(
          (estimationItemParameterName) =>
            ({
              accessor: 'param_' + estimationItemParameterName,
              title: estimationItemParameterName,
              noWrap: true,
              textAlign: 'right',
              hidden: !showParameters,
              render: (item: FormModelItemWithOriginalData) => {
                const formModelItem = item.formModelItem;
                if (!formModelItem.parameters) {
                  throw new Error('estimation item parameters are undefined');
                }
                if (!formModelItem.isIncluded) {
                  return undefined;
                }
                const formModelEstimationItemParameters = formModelItem.parameters;
                const fieldIndex = formModelEstimationItemParameters.findIndex(
                  (x) => x.parameterName === estimationItemParameterName,
                );
                if (fieldIndex === -1) {
                  return undefined;
                }
                const subItemsSum = getSubItemsParameterSum(item, estimationItemParameterName);
                if (subItemsSum !== undefined) {
                  return <FormattedNumber value={subItemsSum} />;
                }
                if (isReadOnly) {
                  return <FormattedNumber value={formModelItem.parameters[fieldIndex].value} />;
                }

                return (
                  <NumericFormField
                    control={control}
                    fieldPath={`estimationItems.${item.formModelItemIndex}.parameters.${fieldIndex}.value`}
                  />
                );
              },
              footer: (
                <FormattedNumber
                  value={_sum(
                    formModelItemsWithOriginalData
                      .filter((item) => item.formModelItem.isIncluded)
                      .map(
                        (item) =>
                          getSubItemsParameterSum(item, estimationItemParameterName) ??
                          item.formModelItem.parameters?.find(
                            (parameter) => parameter.parameterName === estimationItemParameterName,
                          )?.value ??
                          0,
                      ),
                  )}
                />
              ),
            }) as DataTableColumn<FormModelItemWithOriginalData>,
        );

  const resourcingColumns: DataTableColumn<FormModelItemWithOriginalData>[] = sortResourceTypes(
    estimationModel.resourceTypes,
    estimationModel.resourceTypeGroups,
  ).map((resourceType) => {
    return {
      accessor: 'resourceType' + resourceType.id,
      title: (
        <ResourceColumnHeader
          resourceTypeId={resourceType.id}
          displayName={resourceDisplayNames?.[resourceType.id] ?? resourceType.name}
          isReadOnly={isReadOnly}
          onRename={onResourceColumnHeaderRename}
        />
      ),
      noWrap: true,
      textAlign: 'right',
      hidden: !showResources,
      render: (item) => {
        if (item.originalData?.isOmitted) {
          return undefined;
        }

        const formModelItem = item.formModelItem;

        if (!formModelItem.resources) {
          throw new Error('Resources in form model cannot be undefined here.');
        }

        const resourceIndex = formModelItem.resources.findIndex(
          (resource) => resource.resourceTypeId === resourceType.id,
        );

        // If resource type is not found in the form model, return 0
        if (resourceIndex === -1) {
          return <FormattedNumber value={0} />;
        }

        if (isReadOnly || item.originalData?.areFilledSubItemsResources) {
          const resourceValue = formModelItem.resources[resourceIndex].value;
          return <FormattedNumber value={resourceValue} />;
        }

        const formField = (
          <NumericFormField
            control={control}
            fieldPath={`estimationItems.${item.formModelItemIndex}.resources.${resourceIndex}.value`}
            onBlur={onResourceFieldBlur}
          />
        );
        const resourceValue = formModelItem.resources[resourceIndex].value;
        const savedValue = item.originalData?.resources?.find(
          (x) => x.resourceTypeId === resourceType.id,
        )?.value;
        const isSaved = savedValue === resourceValue;
        return (
          <DebugHighlighter precondition={!isSaved} tooltip={`Saved value: ${savedValue}`}>
            {formField}
          </DebugHighlighter>
        );
      },
      footer: (
        <FormattedNumber
          value={formModelItemsWithOriginalData
            .filter((item) => item.formModelItem.isIncluded)
            .map(
              (item) =>
                item.formModelItem.resources?.find(
                  (resource) => resource.resourceTypeId === resourceType.id,
                )?.value ?? 0,
            )
            .reduce((prev, current) => prev + current, 0)}
        />
      ),
    };
  });

  const costsColumns: DataTableColumn<FormModelItemWithOriginalData>[] = sortCostTypes(
    estimationModel.costTypes,
    estimationModel.costTypeGroups,
  )
    .filter((costType) => !hiddenCostTypeColumns?.includes(costType.id))
    .map((costType) => {
      return {
        accessor: 'costType' + costType.id,
        title: costType.name,
        noWrap: true,
        width: 200,
        textAlign: 'right',
        hidden: !costs || !showCosts,
        titleClassName: 'whitespace-break-spaces',
        render: (item: FormModelItemWithOriginalData) => {
          if (item.originalData?.isOmitted) {
            return undefined;
          }

          if (!costs) {
            return undefined;
          }

          if (!item.formModelItem.resources) {
            throw new Error('Resources must be provided to calculate costs');
          }

          const resourceTypes = sortResourceTypes(
            estimationModel.resourceTypes,
            estimationModel.resourceTypeGroups,
          ).filter((resourceType) => resourceType.costTypeId === costType.id);

          const resourcesForCostType = item.formModelItem.resources.filter((resource) =>
            resourceTypes.some((resourceType) => resourceType.id === resource.resourceTypeId),
          );
          const calculatedCost = resourcesForCostType
            .map(
              (resource) =>
                (resource?.value ?? 0) *
                (costs.resourceTypeCosts.find(
                  (resourceTypeCost) => resourceTypeCost.resourceTypeId === resource.resourceTypeId,
                )?.price ?? 0),
            )
            .reduce<number>((sum, resource) => sum + resource, 0);
          return <FormattedNumber value={calculatedCost} />;
        },
        footer:
          costs && estimationItems.length ? (
            <FormattedNumber
              value={_sum(
                estimationItems
                  .filter(
                    (estimationItem) =>
                      !estimationItem.isOmitted &&
                      estimationItem.estimationModelGroupId === group?.id,
                  )
                  .map((estimationItem) => {
                    const resourcesForCostType =
                      estimationItem.resources?.filter((resource) =>
                        costs.resourceTypeCosts.find(
                          (resourceTypeCost) =>
                            resourceTypeCost.costTypeId === costType.id &&
                            resourceTypeCost.resourceTypeId === resource.resourceTypeId,
                        ),
                      ) ?? [];
                    const estimationItemCost = _sum(
                      resourcesForCostType.map(
                        (resource) =>
                          (resource?.value ?? 0) *
                          (costs.resourceTypeCosts.find(
                            (resourceTypeCost) =>
                              resourceTypeCost.costTypeId === costType.id &&
                              resourceTypeCost.resourceTypeId === resource.resourceTypeId,
                          )?.price ?? 0),
                      ),
                    );
                    return estimationItemCost;
                  }),
              )}
            />
          ) : undefined,
      };
    });

  const columns: DataTableColumn<FormModelItemWithOriginalData>[] = [
    {
      accessor: 'hierarchicalOrderReference',
      title: messages.referenceNumber(),
      noWrap: true,
      sortable: false,
      width: 50,
      render: (item) => item.originalData?.reference,
    },
    {
      accessor: 'name',
      title: group?.name ?? messages.emptyTableGroupName(),
      noWrap: false,
      sortable: false,
      width: 300,
      render: (item) => {
        const formModelItem = item.formModelItem;
        const omitReason = !formModelItem.isIncluded ? formModelItem.omitReason : undefined;

        const nameNode =
          onItemRename && !isTemporaryId(formModelItem.id) ? (
            <InlineEditableText
              value={formModelItem.name}
              onSubmit={(name) => onItemRename(formModelItem.id, name)}
              isReadOnly={isReadOnly}
              ariaLabel={messages.renameItem()}
              confirmLabel={messages.saveItemName()}
              cancelLabel={messages.cancelItemRename()}
              className='max-w-full'
              displayClassName='line-clamp-2 text-wrap'
            />
          ) : (
            <span className='line-clamp-2 text-wrap'>{formModelItem.name}</span>
          );

        return (
          <div className='line-clamp-2 text-wrap'>
            {nameNode}
            {omitReason && <p className='text-xs'>{stripHtml(omitReason)}</p>}
          </div>
        );
      },
      footer:
        onAddPhaseClicked &&
        !isReadOnly &&
        !estimationModel.itemGroups.find((x) => x.id === group?.id)?.forbidCustomEstimationItem ? (
          <Button
            variant='primary'
            size='sm'
            IconComponentLeft={PlusCircleIcon}
            onClick={() => onAddPhaseClicked(group?.id)}
            data-testid={`add-phase-button-${group?.id || 'default'}`}
          >
            {messages.addPhaseButton()}
          </Button>
        ) : null,
    },
    {
      accessor: 'buttons',
      title: '',
      noWrap: true,
      width: 150,
      cellsClassName: '!max-w-none',
      render: (item: FormModelItemWithOriginalData) => {
        // TODO: It would be better to disable detail button whenever the estimation item is being processed, but this is a quick workaround to prevent opening detail modal during change of methodology (before receiving response)
        const isTopLevelItemWithMethodologyUnsaved =
          !item.formModelItem.parentId &&
          !!item.formModelItem.methodologyId &&
          item.originalData?.methodology?.id !== item.formModelItem.methodologyId;
        return (
          <Group gap={4} justify='left' wrap='nowrap'>
            <Button
              variant='secondary'
              outlined
              onClick={() => {
                onDetailsClicked(item.formModelItem.id);
              }}
              disabled={!item.originalData || isTopLevelItemWithMethodologyUnsaved}
              isProcessing={item.originalData && isTopLevelItemWithMethodologyUnsaved}
              data-count={item.originalData?.childrenCount ?? 0}
              key='detail'
              size='sm'
              data-tooltip-top=''
              data-title={messages.detailsButton()}
              data-testid={`phase-details-button-${item.formModelItem.id}`}
            >
              <DetailIcon className='-mx-1 h-5 w-5 shrink-0' />
              <span className='sr-only'>{messages.detailsButton()}</span>
            </Button>
            <Button
              variant='secondary'
              outlined
              onClick={() => {
                onAssumptionsClicked(item.formModelItem.id);
              }}
              disabled={!item.originalData}
              key='resource-assumptions'
              size='sm'
              data-count={item.originalData?.assumptionsCount ?? 0}
              data-title={messages.assumptionsButton()}
              data-tooltip-top=''
            >
              <AssumptionIcon className='-mx-1 h-5 w-5 shrink-0' />
              <span className='sr-only'>{messages.assumptionsButton()}</span>
            </Button>
            <Button
              type='button'
              data-atr='test'
              outlined
              variant='secondary'
              size='sm'
              onClick={() => {
                onCommentsClicked(item.formModelItem.id);
              }}
              data-count={item.originalData?.commentsCount ?? 0}
              disabled={!item.originalData}
              data-title={messages.commentsButton()}
              data-tooltip-top=''
            >
              <span className='sr-only'>{messages.commentsButton()}</span>
              <ConversationIcon className='-mx-1 h-5 w-5 shrink-0' />
            </Button>
          </Group>
        );
      },
      hidden: !showButtons,
    },
    {
      accessor: 'responsible',
      title: messages.phaseLead(),
      noWrap: true,
      width: 200,
      hidden: !showResponsible,
      render: (item) => {
        if (!item.formModelItem.isIncluded) {
          return undefined;
        }
        return item.formModelItem.phaseLeadId ? (
          <div className='text-wrap'>
            <UserFullName userId={item.formModelItem.phaseLeadId} />
          </div>
        ) : null;
      },
    },
    {
      accessor: 'method',
      title: messages.methodology(),
      noWrap: true,
      width: 180,
      hidden: !showMethodology,
      cellsClassName: 'flex-grow truncated-table-cell',
      render: (item: FormModelItemWithOriginalData) => {
        if (!formModelEstimationItems) {
          throw new Error('estimation items or its parameters are undefined');
        }
        const formModelItem = item.formModelItem;

        if (!formModelItem.isIncluded) {
          return undefined;
        }
        if (item.originalData?.hasEstimationModelItemParameters) {
          if (
            (item.originalData.estimationModelItemParameters ?? []).some(
              (x) => x.defaultValue !== undefined,
            )
          ) {
            return (
              <>
                {messages.suggestion()} (
                {(item.originalData.estimationModelItemParameters ?? []).map(
                  (estimationModelItemParameter, index) => (
                    <span key={estimationModelItemParameter.id}>
                      {index > 0 ? ' ,' : ''}
                      <FormattedNumber value={estimationModelItemParameter.defaultValue} />
                    </span>
                  ),
                )}
                )
              </>
            );
          }
          return undefined;
        }

        if (isReadOnly || !onMethodologyChange) {
          return methodologies.find((x) => x.id === item.formModelItem.methodologyId)?.name;
        }

        return (
          <DropdownFormField
            control={control}
            fullWidth={true}
            fieldPath={`estimationItems.${item.formModelItemIndex}.methodologyId`}
            options={methodologyDropdownOptions}
            onBeforeChange={onBeforeMethodologyChange}
            onChange={(methodologyId) => onMethodologyChange(item.formModelItem.id, methodologyId)}
          />
        );
      },
    },
    ...parametersColumns,
    {
      accessor: 'estimate',
      title: showEstimateColumn ? messages.estimate() : '',
      width: 150,
      textAlign: 'right',
      render: (item) => {
        if (!showEstimateColumn) return null;
        const formModelItem = item.formModelItem;
        if (!formModelItem.isIncluded) {
          return null;
        }
        const clientSideCalculatedResult = calculatedResultByEstimationItemId.get(formModelItem.id);
        return (
          <DebugHighlighter
            precondition={clientSideCalculatedResult !== item.originalData?.calculatedResult}
            tooltip={`Stored calculated result: ${item.originalData?.calculatedResult}`}
            isTextContent
          >
            <FormattedNumber value={clientSideCalculatedResult} />
          </DebugHighlighter>
        );
      },
      footer: showEstimateColumn ? (
        <FormattedNumber
          value={formModelItemsWithOriginalData
            .filter((item) => item.formModelItem.isIncluded)
            .map((item) => calculatedResultByEstimationItemId.get(item.formModelItem.id) ?? 0)
            .reduce((prev, current) => prev + current, 0)}
        />
      ) : null,
    },
    {
      accessor: 'resourcingCheck',
      title: messages.estimateCheck() + ' Δ',
      noWrap: true,
      textAlign: 'right',
      hidden: !showResources,
      render: (item) => {
        if (item.originalData?.isOmitted) {
          return undefined;
        }

        const formModelItemResourcesSum = (item.formModelItem.resources ?? [])
          .map((x) => x.value ?? 0)
          .reduce((prev, current) => prev + current, 0);

        const calculatedResult = item.originalData?.calculatedResult ?? 0;
        const delta = calculatedResult - formModelItemResourcesSum;

        return (
          <div className='flex items-center justify-end'>
            <div className={delta !== 0 ? 'text-amber-500' : ''}>
              <FormattedNumber value={delta} />
            </div>
          </div>
        );
      },
      footer: (
        <div className='flex items-center justify-end'>
          <div
            className={
              formModelItemsWithOriginalData
                .filter((item) => item.formModelItem.isIncluded)
                .map(
                  (estimationItem) =>
                    (estimationItem.originalData?.calculatedResult ?? 0) -
                    (estimationItem.formModelItem.resources
                      ?.map((x) => x.value ?? 0)
                      .reduce((prev, current) => prev + current, 0) ?? 0),
                )
                .reduce((prev, current) => prev + current, 0) !== 0
                ? 'text-amber-500'
                : ''
            }
          >
            <FormattedNumber
              value={formModelItemsWithOriginalData
                .filter((item) => item.formModelItem.isIncluded)
                .map(
                  (estimationItem) =>
                    (estimationItem.originalData?.calculatedResult ?? 0) -
                    (estimationItem.formModelItem.resources
                      ?.map((x) => x.value ?? 0)
                      .reduce((prev, current) => prev + current, 0) ?? 0),
                )
                .reduce((prev, current) => prev + current, 0)}
            />
          </div>
        </div>
      ),
    },
    ...resourcingColumns,
    ...costsColumns,
    {
      accessor: 'costSummary',
      title: messages.costSummary(),
      noWrap: true,
      width: 200,
      textAlign: 'right',
      hidden: !costs,
      titleClassName: 'whitespace-break-spaces',
      render: (item: FormModelItemWithOriginalData) => {
        if (item.originalData?.isOmitted) {
          return undefined;
        }

        if (!costs) {
          return undefined;
        }

        if (!item.originalData?.resources) {
          throw new Error('Resources must be provided to calculate costs');
        }

        const calculatedCost = item.originalData.resources
          .map(
            (resource) =>
              (resource?.value ?? 0) *
              (costs.resourceTypeCosts.find(
                (resourceTypeCost) => resourceTypeCost.resourceTypeId === resource.resourceTypeId,
              )?.price ?? 0),
          )
          .reduce<number>((sum, resource) => sum + resource, 0);
        return <FormattedNumber value={calculatedCost} />;
      },
      footer:
        costs && formModelItemsWithOriginalData.length ? (
          <FormattedNumber
            value={_sum(
              formModelItemsWithOriginalData
                .filter((estimationItem) => !estimationItem.originalData?.isOmitted)
                .map((estimationItem) => {
                  const estimationItemCost = estimationItem.originalData?.resources
                    ? _sum(
                        estimationItem.originalData.resources.map(
                          (resource) =>
                            (resource?.value ?? 0) *
                            (costs.resourceTypeCosts.find(
                              (resourceTypeCost) =>
                                resourceTypeCost.resourceTypeId === resource.resourceTypeId,
                            )?.price ?? 0),
                        ),
                      )
                    : 0;
                  return estimationItemCost;
                }),
            )}
          />
        ) : undefined,
    },
    {
      accessor: 'isIncluded',
      title: messages.included(),
      noWrap: true,
      hidden: isReadOnly || !onIncludeClicked,
      width: 125,
      render: (item) => {
        if (!onIncludeClicked) {
          throw new Error(
            'OnIncludeClicked is undefined. It should never happen as whole column is hidden in that case.',
          );
        }

        return (
          <div className='flex items-center'>
            <SwitchFormField
              control={control}
              fieldPath={`estimationItems.${item.formModelItemIndex}.isIncluded`}
              onBeforeChange={(checked) => onIncludeClicked(item.formModelItem.id, checked)}
              onChange={(checked) => onIncludeChanged?.(item.formModelItem.id, checked)}
            />
          </div>
        );
      },
    },
  ];

  return (
    <MantineProvider>
      <div className='relative'>
        <div className='pointer-events-none absolute top-0 right-0 z-[5] block h-full w-8 bg-gradient-to-l from-white/80 to-transparent dark:from-black/80' />
        <DataTable<FormModelItemWithOriginalData>
          highlightOnHover
          withTableBorder
          className='table-hover p-2 whitespace-nowrap'
          records={formModelItemsWithOriginalData}
          rowStyle={(record) =>
            !record.formModelItem.name.toLowerCase().includes(searchText.toLowerCase())
              ? { visibility: 'collapse' }
              : undefined
          }
          columns={columns}
          emptyState={estimationItems.length === 0 ? <div>{messages.emptyTableText()}</div> : null}
        />
      </div>
    </MantineProvider>
  );
};

export default EstimationItemsTable;
